import React from 'react'
import { css } from '@emotion/css'
import {
  isFeatureEnabledForLanguage,
  AgentFeature
} from '../../../../components/agent-features'
import { FormattedMessage } from 'react-intl'
import { UsageCellState } from './usage-cell-state'
import { LibraryAgentLanguage } from '../library/library-agent-language'
import { LibraryApplication } from '../../helpers/ui-interfaces'
import { Tooltip } from '@contrast-security-inc/design-system-popovers'
import { Link, Text } from '@contrast-security-inc/design-system-typography'

const tempTextSizeOverrideCss = css`
  /* Align with the rest of the table temporarily, until grids are replaced with Skeletor tables. */
  font-size: 14px;
`

interface UsageCellRowData {
  readonly usage: {
    readonly total: number
    readonly totalUsed: number
  }
  readonly applications?: LibraryApplication[]
  readonly classUsageCount?: number
}

// Usage data is not currently available for this language
export const NotSupported = () => (
  <Tooltip
    body={<FormattedMessage id="detail_libraryUsageCell_NotSupportedTooltip" />}
  >
    <Text
      className={tempTextSizeOverrideCss}
      color="neutral60"
      font="body"
      testId="usage-cell-count-not-supported"
    >
      <FormattedMessage id="detail_libraryUsageCell_NotSupported" />
    </Text>
  </Tooltip>
)

export const Supported = ({
  applications,
  onClick,
  totalUsed,
  total
}: {
  readonly applications?: LibraryApplication[]
  readonly onClick?: () => void
  readonly totalUsed: number
  readonly total: number
}) => {
  const allowClick = typeof onClick !== 'undefined' && totalUsed > 0
  const unused = totalUsed === 0
  const libraryHasMultipleApplications =
    Array.isArray(applications) && applications.length > 1

  const usageCount =
    totalUsed > 0 ? (
      <div>
        <span className={tempTextSizeOverrideCss}>{totalUsed}</span>/
        <Text
          className={tempTextSizeOverrideCss}
          color={allowClick ? undefined : 'neutral60'}
        >
          {total}
        </Text>
      </div>
    ) : null

  const UsageComponent = allowClick ? Link : Text

  return (
    <Tooltip
      body={
        <UsageCellTooltipContent
          libraryHasMultipleApplications={libraryHasMultipleApplications}
        />
      }
    >
      <UsageComponent
        data-testid="usage-cell-count-supported"
        data-e2e={`usage-cell-count-${unused ? 'unused' : 'used'}`}
        onClick={allowClick ? onClick : undefined}
      >
        {unused && (
          <Text
            className={tempTextSizeOverrideCss}
            font="body"
            testId="unused-cell-count-label"
          >
            <FormattedMessage id="title_libraries_unused" />
          </Text>
        )}
        <div>{usageCount}</div>
      </UsageComponent>
    </Tooltip>
  )
}

function UsageCellTooltipContent({
  libraryHasMultipleApplications
}: {
  readonly libraryHasMultipleApplications: boolean
}) {
  return (
    <>
      {libraryHasMultipleApplications && (
        <div
          className={css`
            margin-bottom: 3px;
          `}
        >
          <FormattedMessage id="detail_libraries_usageAcrossAllApplications" />
        </div>
      )}
      <FormattedMessage
        id={
          libraryHasMultipleApplications
            ? 'detail_libraries_maxUsed'
            : 'detail_libraries_totalUsed'
        }
        children={(firstMessage) => (
          <FormattedMessage
            id={
              libraryHasMultipleApplications
                ? 'detail_libraries_maxKnown'
                : 'detail_libraries_totalKnown'
            }
            children={(secondMessage) => (
              <>{`${firstMessage} / ${secondMessage}`}</>
            )}
          />
        )}
      />
    </>
  )
}

export const isObservationsEnabled = ({
  agentFeatures,
  language
}: {
  readonly agentFeatures: AgentFeature[]
  readonly language: LibraryAgentLanguage
}) =>
  isFeatureEnabledForLanguage({
    agentFeatures,
    featureName: 'library-usage-observations',
    language: LibraryAgentLanguage.fromLibraryAgentLanguage(language)
  })

export function UsageCell({
  rowData: { usage, applications, classUsageCount },
  onClick,
  state
}: {
  readonly rowData: UsageCellRowData
  readonly onClick?: (props?: any) => void
  readonly state: UsageCellState
}) {
  return (
    <UsageCellContainer>
      {() => {
        const usageTotal =
          // eslint-disable-next-line eqeqeq
          classUsageCount != undefined ? classUsageCount : usage.totalUsed
        switch (state) {
          case 'counts-supported':
            return (
              <Supported
                totalUsed={usageTotal}
                total={usage.total}
                applications={applications}
              />
            )
          case 'counts-and-observations-supported':
            return (
              <Supported
                totalUsed={usageTotal}
                total={usage.total}
                onClick={onClick}
                applications={applications}
              />
            )
          default:
            // Covers case 'counts-not-supported'
            return <NotSupported />
        }
      }}
    </UsageCellContainer>
  )
}

function UsageCellContainer({ children }) {
  return (
    <div
      className={css`
        width: fit-content;
      `}
    >
      {children()}
    </div>
  )
}
